import React, { useState, useEffect } from "react";
import {
  Row,
  Table,
  Badge,
  ButtonGroup,
  Input,
  Popover,
  PopoverHeader,
  Button,
  PopoverBody,
} from "reactstrap";
import { differenceInDays, format } from "date-fns";
import {
  getProspects,
  updateOrganization,
  archiveOrganization,
} from "../service";
import OrgSummaryPane from "./OrgSummaryPane";
import staticData from "../data/prospects_sample.json";

const USE_STATIC_DATA = false; // Change to false for live API
const formatCurrency = (value) => `₹${(value / 100000).toFixed(2)}L`;

function formatShortDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    month: "short",
    day: "numeric",
  });
}

function formatShortDateTime(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

const formatRelativeTime = (inputDate) => {
  if (!inputDate) return "-";
  const now = new Date();
  const date = new Date(inputDate);
  const diffMs = now - date;

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return "Just now";
  if (diffMinutes < 60)
    return `${diffMinutes} min${diffMinutes > 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
};

const isInactive = (lastActivity) => {
  if (!lastActivity) return false;
  const diffDays =
    (new Date() - new Date(lastActivity)) / (1000 * 60 * 60 * 24);
  return diffDays > 45;
};

const Prospects = () => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "cartValue",
    direction: "desc",
  });
  const [activityFilter, setActivityFilter] = useState("All");
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [activePopover, setActivePopover] = useState(null); // e.g. "met-525" or "target-525"
  const [editState, setEditState] = useState({
    orgId: null,
    met_date: null,
    sub_status: null,
    status: null,
    profile: null,
    build_mode: null,
    size: null,
    demo_date: null,
  });
  const [metFilter, setMetFilter] = useState("All");
  const [subStatusFilter, setSubStatusFilter] = useState("All");
  const [profileFilter, setProfileFilter] = useState("All");
  const [buildModeFilter, setBuildModeFilter] = useState("All");
  const [orgCityFilter, setOrgCityFilter] = useState("All");
  const [sizeFilter, setSizeFilter] = useState("All");
  const [demoFilter, setDemoFilter] = useState("All");
  const [uniqueOrgCities, setUniqueOrgCities] = useState([]);
  const [showOnlyActive, setShowOnlyActive] = useState(false);

  // Options for dropdowns
  const profileOptions = {
    architect_designer: "Architect / Designer",
    builder_contrator: "Builder / Contractor",
    module_factory: "Modular Factory",
  };
  const buildModeOptions = {
    self_cutlist: "Self Cutlist",
    factory_carpenter_cutlist: "Factory / Carpenter Cutlist",
    sub_contract: "Sub-Contract",
    site_finish: "Site Finish",
  };
  const sizeOptions = {
    s: "S",
    m: "M",
    l: "L",
    xl: "XL",
    xxl: "XXL",
  };
  const subStatusOptions = {
    nil: "None",
    target: "Target",
    committed: "Committed",
    trial: "Trial",
    order: "Order",
  };
  const sizeToAmov = {
    s: 1.5,
    m: 3,
    l: 5,
    xl: 7,
    xxl: 10,
  };
  const getAmovValue = (size) => {
    switch (size) {
      case "s":
        return 1.5;
      case "m":
        return 3;
      case "l":
        return 5;
      case "xl":
        return 7;
      case "xxl":
        return 10;
      default:
        return 0;
    }
  };
  const totalAmov = filteredData.reduce(
    (sum, row) => sum + getAmovValue(row.size || 0),
    0
  );

  const fetchProspectsData = async () => {
    const rawData = USE_STATIC_DATA ? staticData : await getProspects();
    const { organizations, projects } = rawData;
    const uniqueOrgCitiesSet = [
      ...new Set(
        Object.values(organizations)
          .map((org) => org.city)
          .filter(Boolean)
      ),
    ];
    setUniqueOrgCities(uniqueOrgCitiesSet);
    const grouped = Object.entries(organizations).map(([orgId, orgData]) => {
      const orgProjects = projects.filter(
        (p) => p.organization_id === parseInt(orgId)
      );
      const realProjects = orgProjects.filter(
        (p) => p.real_order && p.value != null
      );

      const projectCount = realProjects.length;

      const lastActivities = orgProjects
        .filter((p) => p.last_activity_at)
        .map((p) => new Date(p.last_activity_at));

      const lastActivity =
        lastActivities.length > 0
          ? new Date(Math.max(...lastActivities))
          : orgData.activation_date
          ? new Date(orgData.activation_date)
          : null;

      return {
        id: parseInt(orgId),
        name: orgData.name,
        created_at: orgData.created_at ? new Date(orgData.created_at) : null,
        activation_date: orgData.activation_date
          ? new Date(orgData.activation_date)
          : null,
        lastActivity,
        demo_date: orgData.demo_date ? new Date(orgData.demo_date) : null,
        met_date: orgData.met_date ? new Date(orgData.met_date) : null,
        profile: orgData.profile || "",
        build_mode: orgData.build_mode || "",
        size: orgData.size || "",
        sub_status: orgData.sub_status || "nil",
        score: orgData.score || 0,
        projectCount,
        status: orgData.status,
        city: orgData.city,
      };
    });

    setAllData(grouped);
  };

  const sortData = (data, { key, direction }) => {
    return [...data].sort((a, b) => {
      let aVal = a[key] || "";
      let bVal = b[key] || "";

      if (key === "name" || key === "city") {
        return direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      if (key === "amov") {
        const aAmov = sizeToAmov[a.size] || 0;
        const bAmov = sizeToAmov[b.size] || 0;
        return direction === "asc" ? aAmov - bAmov : bAmov - aAmov;
      }
      if (key === "met_date" || key === "demo_date") {
        const aDate = aVal ? new Date(aVal) : null;
        const bDate = bVal ? new Date(bVal) : null;

        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;

        return direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      if (
        key === "profile" ||
        key === "build_mode" ||
        key === "size" ||
        key === "sub_status"
      ) {
        return direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (key === "lastActivity" || key === "activation_date") {
        return direction === "asc"
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }

      return direction === "asc" ? aVal - bVal : bVal - aVal;
    });
  };

  const applyFiltersAndSort = () => {
    let filtered = [...allData];

    // Met Filter
    if (metFilter === "Met") {
      filtered = filtered.filter((d) => d.met_date !== null);
    } else if (metFilter === "Not Met") {
      filtered = filtered.filter((d) => d.met_date === null);
    }
    // Profile Filter
    if (profileFilter !== "All") {
      filtered = filtered.filter((d) => d.profile === profileFilter);
    }

    // Build Mode Filter
    if (buildModeFilter !== "All") {
      filtered = filtered.filter((d) => d.build_mode === buildModeFilter);
    }
    if (orgCityFilter !== "All") {
      filtered = filtered.filter((d) => d.city === orgCityFilter);
    }

    // Size Filter
    if (sizeFilter !== "All") {
      filtered = filtered.filter((d) => d.size === sizeFilter);
    }

    // Demo Filter
    if (demoFilter === "Demo") {
      filtered = filtered.filter((d) => d.demo_date !== null);
    } else if (demoFilter === "No Demo") {
      filtered = filtered.filter((d) => d.demo_date === null);
    }

    // Sub Status Filter
    if (subStatusFilter !== "All") {
      filtered = filtered.filter((d) => d.sub_status === subStatusFilter);
    }

    // Filter by active toggle
    if (showOnlyActive) {
      filtered = filtered.filter((d) => {
        return (
          d.lastActivity &&
          d.activation_date &&
          (new Date(d.lastActivity) - new Date(d.activation_date)) /
            (1000 * 60 * 60) >
            2
        );
      });
    }

    // Separate Active/Inactive and sort
    const prospects = filtered.filter((d) => d.status == "prospect");
    const leads = filtered.filter((d) => d.status == "lead");
    const sortedProspects = sortData(prospects, sortConfig);
    const sortedLeads = sortData(leads, sortConfig);

    setFilteredData([...sortedProspects, ...sortedLeads]);
  };

  const handleSort = (key) => {
    const isAsc = sortConfig.key === key && sortConfig.direction === "asc";
    const newConfig = { key, direction: isAsc ? "desc" : "asc" };
    setSortConfig(newConfig);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  useEffect(() => {
    fetchProspectsData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [
    allData,
    metFilter,
    subStatusFilter,
    profileFilter,
    buildModeFilter,
    orgCityFilter,
    sizeFilter,
    demoFilter,
    showOnlyActive,
    sortConfig,
  ]);

  const handleUpdate = async () => {
    if (!editState.orgId) return;

    const orgId = editState.orgId;
    const updates = {
      ...(editState.met_date !== undefined
        ? { met_date: editState.met_date }
        : {}),
      ...(editState.profile !== undefined
        ? { profile: editState.profile }
        : {}),
      ...(editState.build_mode !== undefined
        ? { build_mode: editState.build_mode }
        : {}),
      ...(editState.size !== undefined ? { size: editState.size } : {}),
      ...(editState.demo_date !== undefined
        ? { demo_date: editState.demo_date }
        : {}),
      ...(editState.sub_status !== undefined && editState.status !== "lead"
        ? {
            sub_status:
              editState.sub_status === "nil" ? null : editState.sub_status,
          }
        : {}),
    };

    await updateOrganization(orgId, updates);

    // Update allData locally
    const updated = allData.map((row) =>
      row.id === editState.orgId
        ? {
            ...row,
            met_date: editState.met_date,
            profile: editState.profile,
            build_mode: editState.build_mode,
            size: editState.size,
            demo_date: editState.demo_date,
            sub_status:
              editState.status !== "lead"
                ? editState.sub_status
                : row.sub_status,
          }
        : row
    );

    setAllData(updated);
    setEditState({}); // Clear edit state
  };

  return (
    <Row className="border p-3 bg-white rounded shadow-sm">
      <div className="d-flex flex-wrap gap-4 mb-3 align-items-center">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold small text-nowrap">Profile:</span>
          <select
            className="form-select"
            value={profileFilter}
            onChange={(e) => setProfileFilter(e.target.value)}
          >
            <option value="All">All</option>
            {Object.entries(profileOptions).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold small text-nowrap">Build Mode:</span>
          <select
            className="form-select form-select-sm"
            value={buildModeFilter}
            onChange={(e) => setBuildModeFilter(e.target.value)}
          >
            <option value="All">All</option>
            {Object.entries(buildModeOptions).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold small text-nowrap">Size:</span>
          <select
            className="form-select form-select-sm"
            value={sizeFilter}
            onChange={(e) => setSizeFilter(e.target.value)}
          >
            <option value="All">All</option>
            {Object.entries(sizeOptions).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold small text-nowrap">Demo:</span>
          <select
            className="form-select"
            value={demoFilter}
            onChange={(e) => {
              setDemoFilter(e.target.value);
            }}
          >
            {["All", "Demo", "No Demo"].map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold small text-nowrap">Met:</span>
          <select
            className="form-select"
            value={metFilter}
            onChange={(e) => {
              setMetFilter(e.target.value);
            }}
          >
            {["All", "Met", "Not Met"].map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold small text-nowrap">Sub Status:</span>
          <select
            className="form-select"
            value={subStatusFilter}
            onChange={(e) => {
              setSubStatusFilter(e.target.value);
            }}
          >
            <option value="All">All</option>
            {Object.entries(subStatusOptions).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold small text-nowrap">City:</span>
          <select
            className="form-select form-select-sm"
            value={orgCityFilter}
            onChange={(e) => setOrgCityFilter(e.target.value)}
          >
            <option value="All">All</option>
            {uniqueOrgCities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="activeToggle"
            checked={showOnlyActive}
            onChange={() => setShowOnlyActive((prev) => !prev)}
          />
          <label
            className="form-check-label ms-2 fw-bold"
            htmlFor="activeToggle"
          >
            Hide Signups Only
          </label>
        </div>
      </div>

      <Table
        bordered
        responsive
        hover
        className="text-center align-middle"
        style={{ fontSize: "0.9rem" }}
      >
        <thead>
          <tr>
            <th
              className="bg-secondary text-white text-start"
              role="button"
              onClick={() => handleSort("name")}
            >
              Customer {renderSortIcon("name")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("amov")}
            >
              AMOV {renderSortIcon("amov")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("profile")}
            >
              Profile {renderSortIcon("profile")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("build_mode")}
            >
              Build Mode {renderSortIcon("build_mode")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("size")}
            >
              Size {renderSortIcon("size")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("sub_status")}
            >
              Sub Status {renderSortIcon("sub_status")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("demo_date")}
            >
              Demo Date {renderSortIcon("demo_date")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("met_date")}
            >
              Met Date {renderSortIcon("met_date")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("activation_date")}
            >
              Signed up {renderSortIcon("activation_date")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("city")}
            >
              City {renderSortIcon("city")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("lastActivity")}
            >
              Activity {renderSortIcon("lastActivity")}
            </th>
            <th
              className="bg-secondary text-white"
              role="button"
              onClick={() => handleSort("score")}
            >
              Score {renderSortIcon("score")}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="text-primary">
            <td className="bg-DDDDDD fw-bold text-start">Total</td>
            <td className="bg-DDDDDD fw-bold">{`₹${totalAmov.toFixed(1)}L`}</td>
            <td className="bg-DDDDDD"></td>
            <td className="bg-DDDDDD"></td>
            <td className="bg-DDDDDD"></td>
            <td className="bg-DDDDDD"></td>
            <td className="bg-DDDDDD"></td>
            <td className="bg-DDDDDD"></td>
            <td className="bg-DDDDDD"></td>
            <td className="bg-DDDDDD"></td>
            <td className="bg-DDDDDD"></td>
            <td className="bg-DDDDDD"></td>
          </tr>
          {filteredData.map((row, idx) => (
            <tr
              key={idx}
              className={idx % 2 === 0 ? "table-light" : ""}
              style={{ cursor: "pointer" }}
              onClick={() => setSelectedOrgId(row.id)}
            >
              <td
                className={`fw-semibold text-start ${
                  row.status === "lead" ? "text-danger" : ""
                }`}
              >
                {row.name}{" "}
                <span className="grey_letter fw-normal">#{row.id}</span>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <span id={`amov-${row.id}`} role="button" className="pointer">
                  {sizeToAmov[row.size] ? `₹${sizeToAmov[row.size]}L` : "-"}
                </span>
              </td>

              <td onClick={(e) => e.stopPropagation()}>
                <span
                  id={`profile-${row.id}`}
                  role="button"
                  onClick={() =>
                    setEditState({
                      orgId: row.id,
                      orgName: row.name,
                      met_date: row.met_date,
                      profile: row.profile,
                      build_mode: row.build_mode,
                      size: row.size,
                      demo_date: row.demo_date,
                      sub_status: row.sub_status,
                      status: row.status,
                    })
                  }
                  className="pointer"
                >
                  {profileOptions[row.profile] || "-"}
                </span>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <span
                  id={`build_mode-${row.id}`}
                  role="button"
                  onClick={() =>
                    setEditState({
                      orgId: row.id,
                      orgName: row.name,
                      met_date: row.met_date,
                      profile: row.profile,
                      build_mode: row.build_mode,
                      size: row.size,
                      demo_date: row.demo_date,
                      sub_status: row.sub_status,
                      status: row.status,
                    })
                  }
                  className="pointer"
                >
                  {buildModeOptions[row.build_mode] || "-"}
                </span>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <span
                  id={`size-${row.id}`}
                  role="button"
                  onClick={() =>
                    setEditState({
                      orgId: row.id,
                      orgName: row.name,
                      met_date: row.met_date,
                      profile: row.profile,
                      build_mode: row.build_mode,
                      size: row.size,
                      demo_date: row.demo_date,
                      sub_status: row.sub_status,
                      status: row.status,
                    })
                  }
                  className="pointer"
                >
                  {sizeOptions[row.size] ? sizeOptions[row.size] : "-"}
                </span>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <span
                  id={`sub_status-${row.id}`}
                  role="button"
                  onClick={() =>
                    setEditState({
                      orgId: row.id,
                      orgName: row.name,
                      met_date: row.met_date,
                      profile: row.profile,
                      build_mode: row.build_mode,
                      size: row.size,
                      demo_date: row.demo_date,
                      sub_status: row.sub_status,
                      status: row.status,
                    })
                  }
                  className="pointer"
                >
                  {subStatusOptions[row.sub_status] || "None"}
                </span>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <span
                  id={`demo-${row.id}`}
                  role="button"
                  onClick={() =>
                    setEditState({
                      orgId: row.id,
                      orgName: row.name,
                      met_date: row.met_date,
                      profile: row.profile,
                      build_mode: row.build_mode,
                      size: row.size,
                      demo_date: row.demo_date,
                      sub_status: row.sub_status,
                      status: row.status,
                    })
                  }
                  className="pointer"
                >
                  {row.demo_date ? formatShortDate(row.demo_date) : "-"}
                </span>
              </td>
              <td onClick={(e) => e.stopPropagation()}>
                <span
                  id={`met-${row.id}`}
                  role="button"
                  onClick={() =>
                    setEditState({
                      orgId: row.id,
                      orgName: row.name,
                      met_date: row.met_date,
                      profile: row.profile,
                      build_mode: row.build_mode,
                      size: row.size,
                      demo_date: row.demo_date,
                      sub_status: row.sub_status,
                      status: row.status,
                    })
                  }
                  className="pointer"
                >
                  {row.met_date ? (
                    formatShortDate(row.met_date)
                  ) : row.demo_date ? (
                    <span className="text-secondary small">
                      {differenceInDays(new Date(), new Date(row.demo_date))}{" "}
                      days!
                    </span>
                  ) : (
                    "-"
                  )}
                </span>
                <Popover
                  isOpen={editState.orgId === row.id}
                  target={`sub_status-${row.id}`}
                  toggle={() =>
                    setEditState({
                      orgId: null,
                      met_date: null,
                      profile: null,
                      build_mode: null,
                      size: null,
                      demo_date: null,
                      sub_status: null,
                      status: null,
                    })
                  }
                >
                  <PopoverHeader>
                    Update {editState.orgName} #{editState.orgId}
                  </PopoverHeader>
                  <PopoverBody>
                    <div className="p-3">
                      {/* Profile Select - Fixed */}
                      <div className="mb-3">
                        <label className="form-label d-block mb-1 fw-bold">
                          Profile
                        </label>
                        <select
                          className="form-select"
                          value={editState.profile || ""}
                          onChange={(e) =>
                            setEditState((prev) => ({
                              ...prev,
                              profile: e.target.value || null,
                            }))
                          }
                        >
                          <option value="">Select Profile</option>
                          {Object.entries(profileOptions).map(
                            ([key, label]) => (
                              <option key={key} value={key}>
                                {label}
                              </option>
                            )
                          )}
                        </select>
                      </div>

                      {/* Build Mode Select - Fixed */}
                      <div className="mb-3">
                        <label className="form-label d-block mb-1 fw-bold">
                          Build Mode
                        </label>
                        <select
                          className="form-select"
                          value={editState.build_mode || ""}
                          onChange={(e) =>
                            setEditState((prev) => ({
                              ...prev,
                              build_mode: e.target.value || null,
                            }))
                          }
                        >
                          <option value="">Select Build Mode</option>
                          {Object.entries(buildModeOptions).map(
                            ([key, label]) => (
                              <option key={key} value={key}>
                                {label}
                              </option>
                            )
                          )}
                        </select>
                      </div>

                      {/* Size Select - Fixed */}
                      <div className="mb-3">
                        <label className="form-label d-block mb-1 fw-bold">
                          Size
                        </label>
                        <select
                          className="form-select"
                          value={editState.size || ""}
                          onChange={(e) =>
                            setEditState((prev) => ({
                              ...prev,
                              size: e.target.value || null,
                            }))
                          }
                        >
                          <option value="">Select Size</option>
                          {Object.entries(sizeOptions).map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {editState.status !== "lead" && (
                        <div className="mb-3">
                          <label className="form-label d-block mb-1 fw-bold">
                            Sub Status
                          </label>
                          <select
                            className="form-select"
                            value={editState.sub_status || "nil"}
                            onChange={(e) =>
                              setEditState((prev) => ({
                                ...prev,
                                sub_status:
                                  e.target.value === "nil"
                                    ? null
                                    : e.target.value,
                              }))
                            }
                          >
                            {Object.entries(subStatusOptions).map(
                              ([key, label]) => (
                                <option key={key} value={key}>
                                  {label}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                      )}

                      <div className="mb-3">
                        <label className="form-label d-block mb-1 fw-bold">
                          Demo Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={
                            editState.demo_date
                              ? new Date(editState.demo_date)
                                  .toISOString()
                                  .slice(0, 10)
                              : ""
                          }
                          onChange={(e) =>
                            setEditState((prev) => ({
                              ...prev,
                              demo_date: e.target.value ? e.target.value : null,
                            }))
                          }
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label d-block mb-1 fw-bold">
                          Met Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={
                            editState.met_date
                              ? new Date(editState.met_date)
                                  .toISOString()
                                  .slice(0, 10)
                              : ""
                          }
                          onChange={(e) =>
                            setEditState((prev) => ({
                              ...prev,
                              met_date: e.target.value ? e.target.value : null,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center bg-EEEEEE px-3 py-2 border-top">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary"
                          onClick={() => handleUpdate(row.id)}
                        >
                          Update
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() =>
                            setEditState({
                              orgId: null,
                              met_date: null,
                              profile: null,
                              build_mode: null,
                              size: null,
                              demo_date: null,
                              sub_status: null,
                              status: null,
                            })
                          }
                        >
                          Cancel
                        </button>
                      </div>
                      <a
                        className="text-danger link ms-auto pointer"
                        onClick={() => {
                          if (
                            window.confirm(
                              "Are you sure you want to archive this organization?"
                            )
                          ) {
                            archiveOrganization(editState.orgId)
                              .then(() => {
                                window.location.reload();
                              })
                              .catch((err) => {
                                console.error(
                                  "Failed to archive organization",
                                  err
                                );
                                alert("Failed to archive organization.");
                              });
                          }
                        }}
                      >
                        Archive
                      </a>
                    </div>
                  </PopoverBody>
                </Popover>
              </td>
              <td
                title={
                  row.activation_date
                    ? new Date(row.activation_date).toLocaleDateString("en-GB")
                    : "-"
                }
              >
                {row.activation_date
                  ? formatShortDateTime(row.activation_date)
                  : "-"}
              </td>
              <td>{row.city ? row.city : "-"}</td>
              <td
                title={
                  row.lastActivity
                    ? row.lastActivity.toLocaleDateString("en-GB")
                    : "-"
                }
                className={
                  row.lastActivity &&
                  row.activation_date &&
                  (new Date(row.lastActivity) - new Date(row.activation_date)) /
                    (1000 * 60 * 60) >
                    2
                    ? "text-primary"
                    : ""
                }
              >
                {row.lastActivity ? formatRelativeTime(row.lastActivity) : "-"}
              </td>
              <td>{row.score}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      {selectedOrgId && (
        <OrgSummaryPane
          organizationId={selectedOrgId}
          onClose={() => setSelectedOrgId(null)}
        />
      )}
    </Row>
  );
};

export default Prospects;
